<template>
    <div class="employee-wrapper" ref="avatar">
        <div class="bounce-container">
            <div class="flip-inner" :class="{ flipped: isFlipped }">
            <div class="face">
                <img class="employee-avatar" :src="avatarUser ?? '/media/default-avatar.png'" @error="e => e.target.src = '/media/default-avatar.png'" alt="employee" />
            </div>
            <div class="face back bounce-animation">
                <div class="message-user">
                    <span>
                        Chúc mừng bạn đang ở vị trí
                        <span style="font-weight: 700">{{ currentLevel }}.</span>
                        Cố gắng lên level tiếp theo nhé !
                    </span>
                </div>
                <img class="employee-level" src="/media/images/hello.gif" alt="employee flipped" />
            </div>
            </div>
        </div>
    </div>
</template>


<script>
import { gsap } from "gsap"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"
import { mapGetters } from "vuex"

gsap.registerPlugin(MotionPathPlugin)

export default {
    name: "EmployeeAvatar",
    props: {
        currentLevel: { type: String, required: true, default: 'Trainee' },
        totalLevels: { type: Number, required: true },
        pathSelector: { type: String, required: true },
        levels: { type: Array, required: true },
        stops: { type: Array, required: false, default: () => [] }
    },
    data() {
        return {
            avatarUser: null,
            motionTween: null,
            currentIndexFloat: 0,
            isInitialized: false,
            flipInterval: null,
            isFlipped: false,
            infoLevels: [
                { id: 1, name: 'Trainee', image: '/media/svg/T.svg' },
                { id: 2, name: 'Junior', image: '/media/svg/J.svg' },
                { id: 3, name: 'Member 1', image: '/media/svg/M1.svg' },
                { id: 4, name: 'Member 2', image: '/media/svg/M2.svg' },
                { id: 5, name: 'Senior 1', image: '/media/svg/S1.png' },
                { id: 6, name: 'Senior 2', image: '/media/svg/S2.svg' }
            ],
        }
    },
    computed: {
        level() {
            return this.infoLevels.find(item => item.name === this.currentLevel) || null
        },
        ...mapGetters(["currentUser"])
    },
    mounted() {
        this.currentIndexFloat = 0
        this.alignToProgress()
        this.syncToLevel()
        this.avatarUser = this.currentUser?.image
        this.startAutoFlip()
        window.addEventListener('resize', this.onResize, { passive: true })
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        if (this.motionTween) this.motionTween.kill()
        if (this.flipInterval) clearInterval(this.flipInterval)
    },
    watch: {
        currentLevel() {
            this.syncToLevel()
        }
    },
    methods: {
        startAutoFlip() {
            this.flipInterval = setInterval(() => {
                this.isFlipped = !this.isFlipped
            }, 3000)
        },
        onResize() {
            this.alignToProgress()
        },
        getPathEl() {
            return document.querySelector(this.pathSelector)
        },
        getTargetIndex() {
            const names = this.infoLevels.map(l => l.name)
            const idx = names.indexOf(this.currentLevel)
            return idx >= 0 ? idx : 0
        },
        alignToProgress() {
            const path = this.getPathEl()
            const avatar = this.$refs.avatar
            if (!path || !avatar) return
            const total = path.getTotalLength()
            const stops = Array.isArray(this.stops) ? this.stops : []
            const n = Math.max(1, stops.length)
            const idxFloat = Math.min(n - 1, Math.max(0, this.currentIndexFloat))
            const i0 = Math.floor(idxFloat)
            const i1 = Math.min(n - 1, i0 + 1)
            const frac = idxFloat - i0
            const s0 = stops[i0] || { t: 0, offset: { x: 0, y: 0 } }
            const s1 = stops[i1] || s0
            const t0 = typeof s0.t === 'number' ? s0.t : 0
            const t1 = typeof s1.t === 'number' ? s1.t : t0
            const t = t0 + (t1 - t0) * frac
            const lengthAt = total * t
            const p = path.getPointAtLength(lengthAt)
            const off0 = (s0 && s0.offset) ? s0.offset : { x: 0, y: 0 }
            const off1 = (s1 && s1.offset) ? s1.offset : off0
            let ox = (off0.x || 0) + ((off1.x || 0) - (off0.x || 0)) * frac
            let oy = (off0.y || 0) + ((off1.y || 0) - (off0.y || 0)) * frac
            const normal0 = (typeof s0.normal === 'number') ? s0.normal : 0
            const normal1 = (typeof s1.normal === 'number') ? s1.normal : normal0
            let normal = normal0 + (normal1 - normal0) * frac
            const offsetSpace = s0 && typeof s0.offsetSpace === 'string' ? s0.offsetSpace : 'svg'
            let px = p.x
            let py = p.y
            if (normal !== 0) {
                const delta = Math.max(0.5, total * 0.0005)
                const p2 = path.getPointAtLength(Math.min(total, Math.max(0, lengthAt + delta)))
                const tx = p2.x - p.x
                const ty = p2.y - p.y
                const tl = Math.hypot(tx, ty) || 1
                const nx = (-ty / tl) * normal
                const ny = (tx / tl) * normal
                px += nx
                py += ny
            }
            if (offsetSpace === 'svg') {
                px += ox
                py += oy
            }
            const svg = path.ownerSVGElement
            const ctm = svg.getScreenCTM && svg.getScreenCTM()
            const svgPoint = svg.createSVGPoint ? svg.createSVGPoint() : null
            const container = this.$el.parentElement
            const containerRect = container.getBoundingClientRect()
            let sx = px
            let sy = py
            if (svgPoint && ctm) {
                svgPoint.x = px
                svgPoint.y = py
                const screen = svgPoint.matrixTransform(ctm)
                sx = screen.x - containerRect.left
                sy = screen.y - containerRect.top
            }
            if (offsetSpace === 'screen') {
                sx += ox
                sy += oy
            }
            gsap.set(avatar, { x: sx - 50, y: sy })
        },
        syncToLevel() {
            const targetIndex = this.getTargetIndex()
            const path = this.getPathEl()
            if (!path) return
            if (this.motionTween) this.motionTween.kill()
            if (!this.isInitialized) {
                this.currentIndexFloat = 0
                this.alignToProgress()
                this.isInitialized = true
            }
            this.motionTween = gsap.to(this, {
                duration: 3.2,
                ease: "power1.inOut",
                currentIndexFloat: targetIndex,
                onUpdate: this.alignToProgress,
                onComplete: () => { this.alignToProgress() }
            })
        }
    }
}
</script>

<style scoped>
.employee-wrapper {
    position: absolute;
    perspective: 1000px;
    width: 78px;
    height: 78px;
}

.flip-inner {
    transform-style: preserve-3d;
    transition: transform 0.6s ease;
}

.flip-inner.flipped {
    transform: rotateY(180deg);
}

.face {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
}

.message-user {
    border: 1px solid gray;
    position: absolute;
    top: 0;
    left: 100px;
    display: flex;
    width: 200px;
    background: white;
    border-radius: 10px;
    padding: 8px;
}

.bounce-animation {
  cursor: pointer;
  animation: bounce 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.employee-level {
    width: 78px;
    height: 78px;
    border-radius: 50%;
    object-fit: cover;
}

.employee-avatar {
    margin-top: 1px;
    margin-left: 2px;
    width: 73px;
    height: 73px;
    border-radius: 50%;
    object-fit: cover;
}

.back {
    transform: rotateY(180deg);
}

/* Removed hover effects - now using auto-flip */

@media only screen and (max-width: 1024px) {
    .employee-level {
        width: 35px;
        height: 35px;
    }

    .employee-avatar {
        width: 33px;
        height: 33px;
        border-radius: 50%;
        object-fit: cover;
    }
}

</style>
